/**
 * The one question step 1 asks, and what the machine writes because of the answer.
 *
 * **The service is not a field.** `POST /api/orders` has nowhere to put it and the
 * rider's app has nowhere to show it, so adding a key would be a value nobody reads.
 * Instead it opens `itemDetails.description` — a field the API requires, that the
 * customer used to fill in by hand directly above an item list asking the same
 * question again.
 *
 * Writing the order twice is one of the reasons the old form reads as cluttered, so the
 * box is gone and this writes the sentence. It is the only free-text field on the order
 * that a machine composes, and it earns that because the whole field was a duplicate —
 * nothing a person said is being displaced.
 */

/**
 * Two, and there will not be a third.
 *
 * Grocery, gamot and pagkain were considered and dropped: each is a *pabili* with a
 * different word in front, none of them changes a single step, and the only real benefit
 * — filtering the store listing by category — died with the decision to keep the listing
 * to five fields. What would be left is a longer list to read on the first screen, which
 * is the exact problem this redesign exists to solve.
 */
export const SERVICES = [
  {
    value: 'pabili',
    label: 'Pabili',
    hint: 'May bibilhin ka. Ibibili ka ng rider at ihahatid sa iyo.',
  },
  {
    value: 'padala',
    label: 'Padala',
    hint: 'May ipapadala ka. Kukunin ng rider at ihahatid sa taong pinadalhan.',
  },
]

const VALUES = SERVICES.map((service) => service.value)

/** Anything the form has not answered yet reads as pabili — the ordinary case. */
const serviceOf = (state) => (VALUES.includes(state?.service) ? state.service : 'pabili')

export const isPadala = (state) => serviceOf(state) === 'padala'

/**
 * Padala has nothing to buy, so step 5 does not exist on it and `estimatedTotal` and
 * `maxBudget` both go as zero. `maxBudget >= estimatedTotal` still holds at 0.
 */
export const needsBudget = (state) => !isPadala(state)

/**
 * A place, in the plainest words available for it.
 *
 * Preference order matters. `pickupSource` is what the customer chose from the listing
 * or typed — a shop name. `label` is whatever the place search returned, which is a
 * street when the search worked and the coordinates as text when they pasted a map link.
 * The shop name is the one a rider can act on.
 */
const placeOf = (location, source) => {
  const candidates = [source, location?.label]

  for (const candidate of candidates) {
    const text = typeof candidate === 'string' ? candidate.trim() : ''
    if (text) return text
  }

  return 'napiling lokasyon'
}

/**
 * Kept inside the API's 3–1000, whatever the inputs were.
 *
 * The ceiling is real: a Places result can be long, and two of them plus the connecting
 * words can pass a thousand characters. There is no field-level error to catch it — the
 * API answers with one opaque sentence — so an over-long description would read to the
 * customer as an order that simply refuses to be placed.
 */
const withinBounds = (text) => text.slice(0, 1000)

/**
 * @param {object} state — the form's own state
 * @returns {string} — `itemDetails.description`, ready to send
 */
export function describeOrder(state) {
  if (isPadala(state)) {
    const from = placeOf(state?.pickup, state?.pickupSource)
    const to = placeOf(state?.dropoff)

    return withinBounds(`Padala mula sa ${from} papuntang ${to}`)
  }

  return withinBounds(`Pabili mula sa ${placeOf(state?.pickup, state?.pickupSource)}`)
}
